import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useModalConfirm, useProduceSummary } from "@ryde/redux";
import {
  buildProductionCriteriaCellButton,
  ConfirmResult,
  DrawerType,
  hasActiveProductionRecurring,
  IProduceButtonDto,
  parseBoolean,
  ProduceButtonTodayState,
  resolveProduceDirectionsServicePayload,
  useDrawers,
  useTimezone,
} from "@ryde/shared";

import { MuiButton } from "../../controls";
import { IRenderColumnTemplate } from "./_core";
import { ProductionActionButtonStyled } from "./production-action-button-cell.styled";

export const ProductionActionButtonCellTemplate: FC<IRenderColumnTemplate> = ({
  rowOriginal,
}) => {
  const produceButtonDto = rowOriginal as IProduceButtonDto;
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { confirmDialogResult } = useModalConfirm();
  const { openDrawer } = useDrawers();
  const { buildProduceSummaryForCellButton } = useProduceSummary();

  const produceJoin = useEvent(async (message: string) => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message,
    });

    if (confirmationResult === ConfirmResult.OK) {
      const criteria = buildProductionCriteriaCellButton(
        produceButtonDto,
        timezone,
      );
      const summaryItems =
        await buildProduceSummaryForCellButton(produceButtonDto);
      openDrawer({
        drawerProps: {
          type: DrawerType.ProductionPreview,
          action: produceButtonDto.productionAction,
          productionType: produceButtonDto.productionType,
          rideTypeId: produceButtonDto.rideTypeId,
          ...resolveProduceDirectionsServicePayload(
            produceButtonDto.metadata?.directionsService,
          ),
          summaryItems,
          criteria,
          chain: produceButtonDto.metadata?.chain,
          sendToSupplierAfterProduce: parseBoolean(
            produceButtonDto.metadata?.sendToSupplierAfterProduce,
          ),
          doNotLockBookings: parseBoolean(
            produceButtonDto.metadata?.doNotLockBookings,
          ),
          // A manual click must honour the button's dock setting just like its scheduled runs do.
          assignDocksDuringProduction:
            produceButtonDto.metadata?.assignDocksDuringProduction,
        },
      });
    }
  });

  const handleProduce = useEvent((message: string) => {
    produceJoin(message);
  });

  const handleStartProduceClick = useEvent(() => {
    handleProduce(t(`general.BULK_PRODUCE_MESSAGE`));
  });

  const handleAutoProduceClick = useEvent(() => {
    handleProduce(t(`general.confirmProduceNow`));
  });

  const isActiveRecurring = hasActiveProductionRecurring(
    produceButtonDto.metadata?.recurring,
  );

  return (
    <ProductionActionButtonStyled>
      {isActiveRecurring && (
        <MuiButton
          muiVariant="outlined"
          muiSize="small"
          muiStyle={"warning"}
          e2eData="produce-acton-button"
          onClick={handleAutoProduceClick}
        >
          {t("produceButton.gridLabel.auto_recurring")}
        </MuiButton>
      )}
      {!isActiveRecurring && (
        <MuiButton
          muiVariant="outlined"
          muiSize="small"
          muiStyle={
            produceButtonDto.todayState === ProduceButtonTodayState.ActiveToday
              ? "primary"
              : "warning"
          }
          e2eData="produce-acton-button"
          onClick={handleStartProduceClick}
        >
          {t("produceButton.gridLabel.start_production_button")}
        </MuiButton>
      )}
    </ProductionActionButtonStyled>
  );
};
